import { useEffect, useMemo, useState } from "react";
import {
  buildPricedMenuIndex,
  isFabricPriced,
  isGarmentPriced,
  isStylePriced,
} from "./pricedMenu";
import { FabricType } from "../typings";
import { ITypeItem } from "../typings/interfaces/pos/typeItem";
import { FabricTypeResponse } from "../typings/interfaces/responses/fabricTypeResponse";
import { useAppSelector } from "./reduxHooks";

const usePricing = () => {
  const { selectedServiceType, previousServiceType, selectedCartItem } =
    useAppSelector((state) => state.cart);
  const [subCategories, setSubCategories] = useState<Array<ITypeItem>>([]);
  const [materialTypes, setMaterialTypes] = useState<Array<ITypeItem>>([]);
  const [secondSubCategories, setSecondSubCategories] = useState<
    Array<ITypeItem>
  >([]);

  const {
    categoryResponse: categoryList,
    subCategoryResponse: garmentType,
    secondSubCategoryResponse: secondSubCategory,
    dryCleanResponse: dryCleanPrice,
    fabricTypesResponse: fabricType,
    categoryRelationshipResponse: categoryRelationship,
    categoryMaterialRelationship: categoryMaterialRelationship,
    unavailableMenuItems,
  } = useAppSelector((state) => state.pricing);

  // Items another price list prices but this customer's list doesn't; hidden
  // so they can't be picked at $0. Keys: "category:garment:style" and
  // "category:material".
  const hiddenStyles = new Set(
    (unavailableMenuItems?.styles ?? []).map(
      (s) => `${s.categoryId}:${s.subCategoryId}:${s.secondSubCategoryId}`
    )
  );
  const hiddenFabrics = new Set(
    (unavailableMenuItems?.fabrics ?? []).map((f) => `${f.categoryId}:${f.materialId}`)
  );

  // Combinations priced above $0 in the loaded list (or at $0 for services
  // that allow it); everything else is hidden. Null while prices load, so
  // menus aren't emptied meanwhile.
  const pricedIndex = useMemo(
    () =>
      buildPricedMenuIndex(
        dryCleanPrice?.body,
        new Set((categoryList?.body ?? []).filter((c) => c.allowZeroPrice).map((c) => c.id))
      ),
    [dryCleanPrice, categoryList]
  );
  const selectedGarmentId = selectedCartItem?.garmentType?.id;
  const selectedStyleId = selectedCartItem?.garmentStyle?.id;
  const selectedFabricId = selectedCartItem?.materialType?.id;

  useEffect(() => {
    getSecondSubCategories();
    getGarmentTypes();
  }, [categoryRelationship, unavailableMenuItems, pricedIndex]);

  useEffect(() => {
    getMaterialTypes();
  }, [
    categoryMaterialRelationship,
    selectedServiceType,
    unavailableMenuItems,
    pricedIndex,
    selectedGarmentId,
    selectedStyleId,
  ]);

  const getGarmentTypes = () => {
    const serviceTypeId =
      selectedServiceType?.id === -100000
        ? previousServiceType?.id
        : selectedServiceType?.id;
    const garmentTypes: Array<ITypeItem> | undefined =
      categoryRelationship?.body
        .filter(
          (item) =>
            item.categoryId === serviceTypeId &&
            // a garment stays while any of its styles is available
            !hiddenStyles.has(
              `${item.categoryId}:${item.subCategoryId}:${item.secondSubCategoryId}`
            ) &&
            isGarmentPriced(pricedIndex, serviceTypeId, item.subCategoryId)
        )
        .map((item) => {
          return {
            name: item.subCategoryName,
            id: item.subCategoryId,
            icon: item.icon,
          };
        });
    // filter gamentTypes as unique list from id
    const uniqueList = garmentTypes?.filter(
      (v, i, a) => a.findIndex((t) => t.id === v.id) === i
    );
    if (uniqueList) setSubCategories(uniqueList);
  };

  const getMaterialTypes = () => {
    const serviceTypeId =
      selectedServiceType?.id === -100000
        ? previousServiceType?.id
        : selectedServiceType?.id;
    const materialTypes: Array<ITypeItem> | undefined =
      categoryMaterialRelationship?.body
        .filter(
          (item) =>
            item.categoryId === serviceTypeId &&
            !hiddenFabrics.has(`${item.categoryId}:${item.materialId}`) &&
            // once a garment (and style) is picked, only fabrics it's priced in
            isFabricPriced(pricedIndex, serviceTypeId, item.materialId, selectedGarmentId, selectedStyleId)
        )
        .map((item) => {
          return { name: item.materialName, id: item.materialId };
        });
    // filter gamentTypes as unique list from id
    const uniqueList = materialTypes?.filter(
      (v, i, a) => a.findIndex((t) => t.id === v.id) === i
    );
    if (uniqueList) setMaterialTypes(uniqueList);
  };

  const getSecondSubCategories = () => {
    const serviceTypeId =
      selectedServiceType?.id === -100000
        ? previousServiceType?.id
        : selectedServiceType?.id;
    const secondSubCategories: Array<ITypeItem> | undefined =
      categoryRelationship?.body
        .filter((item) => item.categoryId === serviceTypeId)
        .map((item) => {
          return { name: item.subCategoryName, id: item.subCategoryId };
        });
    // filter gamentTypes as unique list from id
    const uniqueList = secondSubCategories?.filter(
      (v, i, a) => a.findIndex((t) => t.id === v.id) === i
    );
    if (uniqueList) setSecondSubCategories(uniqueList);
  };

  const getGarmentStyleList = (garmentTypeId: number) => {
    const serviceTypeId =
      selectedServiceType?.id === -100000
        ? previousServiceType?.id
        : selectedServiceType?.id;
    const secondSubCategories: Array<ITypeItem> | undefined =
      categoryRelationship?.body
        .filter(
          (item) =>
            item.categoryId === serviceTypeId &&
            item.subCategoryId === garmentTypeId &&
            !hiddenStyles.has(
              `${item.categoryId}:${item.subCategoryId}:${item.secondSubCategoryId}`
            ) &&
            // no $0 styles; once a fabric is picked, only styles priced with it
            isStylePriced(pricedIndex, serviceTypeId, item.subCategoryId, item.secondSubCategoryId, selectedFabricId)
        )
        .map((item) => {
          return {
            name: item.secondSubCategoryName,
            id: item.secondSubCategoryId,
            pieceCount: item.pieceCount,
          };
        });
    // filter gamentTypes as unique list from id
    const uniqueList = secondSubCategories?.filter(
      (v, i, a) => a.findIndex((t) => t.id === v.id) === i
    );
    // if(uniqueList)
    // setSecondSubCategories(uniqueList);
    return uniqueList;
  };

  // const getDryCleanPrice = (
  //   categoryId: number,
  //   subCategoryId: number,
  //   secondSubCategoryId: number,
  //   fabricType: FabricTypeResponse
  // ) => {
  //   // const dcPrice = dryCleanPrice?.body.filter(
  //   //   (item) =>
  //   //     item.category === categoryId &&
  //   //     item.subCategory === subCategoryId &&
  //   //     item.secondSubCategory === secondSubCategoryId &&
  //   //     item.fabricType === fabricType.id
  //   // );
  //   return dcPrice && dcPrice?.length > 0 ? dcPrice[0].price : 0;
  // };

  return {
    categoryList,
    subCategories,
    materialTypes,
    secondSubCategories,
    getGarmentStyleList,
    // getDryCleanPrice,
  };
};

export default usePricing;
